'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { detectRegion, saveConsent, getConsent, logConsentAction } from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

export default function CookieBanner() {
  const [region, setRegion] = useState<ConsentRegion>('none');
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detectedRegion = detectRegion();
    setRegion(detectedRegion);

    const existingConsent = getConsent();
    if (!existingConsent && detectedRegion !== 'none') {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent(region, { necessary: true, analytics: true, marketing: true, preferences: true });
    logConsentAction('accept_all', { region });
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    saveConsent(region, { necessary: true, analytics: false, marketing: false, preferences: false });
    logConsentAction('reject_all', { region });
    setShowBanner(false);
  };

  const handleCCPADismiss = () => {
    saveConsent(region, { necessary: true, analytics: true, marketing: false, preferences: true });
    logConsentAction('ccpa_dismiss', { region });
    setShowBanner(false);
  };

  if (!showBanner || region === 'none') return null;

  return (
    <>
      {region === 'gdpr' && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t p-6 backdrop-blur-sm" style={{ backgroundColor: 'rgba(8, 11, 18, 0.95)', borderColor: 'rgba(31, 34, 39, 0.3)' }}>
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2">
                <h3 className="font-semibold text-base mb-2" style={{ color: 'oklch(95% .005 265)' }}>Cookie Consent</h3>
                <p className="text-sm mb-4" style={{ color: 'oklch(65% .01 265)' }}>
                  We use cookies to enhance your experience, analyze site traffic, and serve personalized content. Learn more in our <Link href="/privacy-policy" className="underline hover:text-white transition-colors">Privacy Policy</Link> and <Link href="/cookies" className="underline hover:text-white transition-colors">Cookie Policy</Link>.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:col-span-1">
                <button onClick={handleRejectAll} className="px-4 py-2.5 rounded-lg border text-sm font-medium transition-all" style={{ borderColor: 'rgba(31, 34, 39, 0.5)', color: 'oklch(95% .005 265)' }}>Reject All</button>
                <button onClick={() => setShowPreferences(true)} className="px-4 py-2.5 rounded-lg border text-sm font-medium transition-all" style={{ borderColor: 'rgba(31, 34, 39, 0.5)', color: 'oklch(95% .005 265)' }}>Manage Preferences</button>
                <button onClick={handleAcceptAll} className="px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all" style={{ backgroundColor: 'rgb(43, 98, 239)' }}>Accept All</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {region === 'ccpa' && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t p-6 backdrop-blur-sm" style={{ backgroundColor: 'rgba(8, 11, 18, 0.95)', borderColor: 'rgba(31, 34, 39, 0.3)' }}>
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-sm" style={{ color: 'oklch(65% .01 265)' }}>
                We respect your privacy. Under CCPA, you have the right to know what personal information we collect. Learn more in our <Link href="/privacy-policy#do-not-sell" className="underline hover:text-white transition-colors">Do Not Sell My Personal Information</Link> section.
              </p>
              <button onClick={handleCCPADismiss} className="px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all whitespace-nowrap" style={{ backgroundColor: 'rgb(43, 98, 239)' }}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {showPreferences && <ConsentPreferenceCenter region={region} onClose={() => setShowPreferences(false)} />}
    </>
  );
}
